import React, { ReactElement, useMemo } from 'react';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { InspectionTemplateTranslationKeys, InspectionTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { Box, Divider, TextField, Typography } from '@mui/material';

import { MemoFormSection } from 'components/template/wizard/FormSection';
import { MemoProcessSelect } from 'components/template/wizard/ProcessSelect';
import { MemoChecklistBuilder } from 'components/template/wizard/ChecklistBuilder';
import { RichTextEditor } from 'components/common/RichTextEditor';
import { CollapsibleRichTextComment } from 'components/inspection/CollapsibleRichTextComment';
import { InspectionCommentLabelVariant } from 'components/inspection/CollapsibleInspectionComment/types';
import { fieldLabelSx } from 'shared/styles/typography';
import { TemplateWizardPresentationalProps } from './types';
import {
  GENERAL_NORM_DOM_ELEMENT_ID,
  indexGraphicUploadersByEntityId,
  PROCESSES_DOM_ELEMENT_ID,
  SUMMARY_DOM_ELEMENT_ID,
} from './helpers';
import { GraphicUploader } from 'components/common/graphicUploader';
import { SubmitButtons } from './submitButtons';
import { MemoVisibleFieldsSelect } from './visibleFieldsSelect';
import ViewWrapper from 'components/core/Layout/ViewWrapper';
import { FormPageHeader } from 'components/inspection/FormPageHeader';
import { SubmitFooter } from 'components/submitFooter';
import {
  inspectionFormFooterSpacerSx,
  inspectionFormSectionDividerSx,
} from 'components/inspection/layout';

const MemoTextField = React.memo(TextField);

function Presentational(
  props: TemplateWizardPresentationalProps
): ReactElement {
  const intl = useIntl();

  const {
    pageTitleId,
    defaultValues,
    process,
    processes,
    sections,
    items,
    errors,
    isSaveDraftInProgress,
    isPublishing,
    handleSaveDraft,
    handlePublish,
    handleCancel,
    handleProcessChange,
    handleTemplateSummaryChange,
    handleGeneralNormDescriptionChange,
    generalNormDescription,
    generalNormConfig,
    handleGeneralNormConfigChange,
    graphicUploader,
    handleSectionTitleChange,
    handleSectionDescriptionChange,
    handleSectionGraphicUploaderInit,
    handleAddSection,
    handleDeleteSection,
    handleItemSummaryChange,
    handleItemDescriptionChange,
    handleItemGraphicUploaderInit,
    handleAddItem,
    handleDeleteItem,
    handleReorderSection,
    handleReorderItem,
    handleAddAnswer,
    handleUpdateAnswer,
    handleDeleteAnswer,
    checklistValidationFocus,
  } = props;

  const pageTitle = formatTranslation(intl, unsafeTranslationKey(pageTitleId));

  const selectedProcess = useMemo(
    () => processes.find((item) => item._id === process),
    [process, processes]
  );

  const defaultSections = useMemo(
    () => defaultValues.sections || [],
    [defaultValues.sections]
  );

  const defaultItems = useMemo(
    () =>
      defaultValues.checklist.map((item) => ({
        ...item,
        parentSectionId: item.parentSectionId || null,
        order: item.order || '',
      })),
    [defaultValues.checklist]
  );

  const itemGraphicUploaders = useMemo(
    () => indexGraphicUploadersByEntityId(items),
    [items]
  );

  const sectionGraphicUploaders = useMemo(
    () => indexGraphicUploadersByEntityId(sections),
    [sections]
  );

  const isPosting = isSaveDraftInProgress || isPublishing;

  return (
    <>
      <ViewWrapper>
        <FormPageHeader title={pageTitle} process={selectedProcess} />

        <MemoFormSection
          variant="page"
          titleId="inspection_template_wizard_basic_info"
          descriptionId="inspection_template_wizard_basic_info_description"
        >
          <MemoTextField
            multiline
            size="medium"
            id={`${SUMMARY_DOM_ELEMENT_ID}`}
            label={formatTranslation(intl, InspectionTemplateTranslationKeys.wizardSummary)}
            variant="outlined"
            onChange={handleTemplateSummaryChange}
            required
            error={Boolean(errors.summary)}
            helperText={
              errors.summary &&
              formatTranslation(
                intl,
                unsafeTranslationKey(errors.summary.helperText.id),
                errors.summary.values
              )
            }
            defaultValue={defaultValues.summary}
            sx={{
              width: '100%',
              marginBottom: '24px',
              borderRadius: '4px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
              },
            }}
          />
          <Box id={`${PROCESSES_DOM_ELEMENT_ID}`}>
            <MemoProcessSelect
              processes={processes}
              value={process}
              onChange={handleProcessChange}
              error={Boolean(errors.process)}
              helperText={
                errors.process &&
                formatTranslation(
                  intl,
                  unsafeTranslationKey(errors.process.helperText.id)
                )
              }
              required
            />
          </Box>
        </MemoFormSection>

        <Box
          sx={{
            marginTop: '24px',
            marginBottom: '24px',
            paddingLeft: '5%',
            paddingRight: '5%',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <MemoVisibleFieldsSelect
            generalNormConfig={generalNormConfig}
            handleGeneralNormConfigChange={handleGeneralNormConfigChange}
          />
        </Box>

        <Divider sx={inspectionFormSectionDividerSx} />

        <MemoFormSection
          variant="page"
          titleId="inspection_template_wizard_general_applied_standards"
          descriptionId="inspection_template_wizard_general_applied_standards_description"
        >
          <Box id={GENERAL_NORM_DOM_ELEMENT_ID}>
            <Typography component='p' sx={fieldLabelSx}>
              {formatTranslation(
                intl,
                InspectionTemplateTranslationKeys.wizardStandardDescription
              )}
            </Typography>
            <CollapsibleRichTextComment
              value={generalNormDescription}
              labelVariant={InspectionCommentLabelVariant.section}
            >
              <RichTextEditor
                value={generalNormDescription}
                onChange={handleGeneralNormDescriptionChange}
                error={Boolean(errors.description)}
                maxLength={10000}
                minRows={3}
                dense
                placeholder={formatTranslation(
                  intl,
                  InspectionTranslationKeys.commentAddPlaceholder
                )}
              />
            </CollapsibleRichTextComment>
          </Box>
          <Box sx={{ marginTop: '24px' }}>
            <GraphicUploader {...graphicUploader} />
          </Box>
        </MemoFormSection>

        <Divider sx={inspectionFormSectionDividerSx} />

        <MemoFormSection
          variant="page"
          titleId="inspection_template_wizard_checklist"
          descriptionId="inspection_template_wizard_checklist_description"
        >
          <MemoChecklistBuilder
            sections={sections}
            items={items}
            defaultSections={defaultSections}
            defaultItems={defaultItems}
            onSectionTitleChange={handleSectionTitleChange}
            onSectionDescriptionChange={handleSectionDescriptionChange}
            onSectionGraphicUploaderInit={handleSectionGraphicUploaderInit}
            onAddSection={handleAddSection}
            onDeleteSection={handleDeleteSection}
            onItemSummaryChange={handleItemSummaryChange}
            onItemDescriptionChange={handleItemDescriptionChange}
            onItemGraphicUploaderInit={handleItemGraphicUploaderInit}
            onAddItem={handleAddItem}
            onDeleteItem={handleDeleteItem}
            onReorderSection={handleReorderSection}
            onReorderItem={handleReorderItem}
            sectionGraphicUploaders={sectionGraphicUploaders}
            itemGraphicUploaders={itemGraphicUploaders}
            onAddAnswer={handleAddAnswer}
            onUpdateAnswer={handleUpdateAnswer}
            onDeleteAnswer={handleDeleteAnswer}
            checklistValidationFocus={checklistValidationFocus}
          />
        </MemoFormSection>

        <Box sx={inspectionFormFooterSpacerSx} />
      </ViewWrapper>

      <SubmitFooter
        onCancel={handleCancel}
        isPosting={isPosting}
        rightContent={
          <SubmitButtons
            onSaveDraft={handleSaveDraft}
            saveDraftInProgress={isSaveDraftInProgress}
            publishing={isPublishing}
            onPublish={handlePublish}
          />
        }
      />
    </>
  );
}

export const MemoPresentational = React.memo(Presentational);
